/**
 * Exportação de uma tabela para planilha do Excel, montada no navegador, e a
 * leitura de uma planilha escolhida pela pessoa.
 *
 * Papel: aqui ficam a `Planilha` — o nome do arquivo e as abas, cada uma com
 * nome, cabeçalhos, linhas e, quando pedida, a largura de cada coluna —,
 * `exportarPlanilha`, que a transforma num `.xlsx` e entrega o arquivo ao
 * navegador como download, sem passar pela API, e `lerPlanilha`, que abre um
 * `.xlsx` no navegador e devolve as abas e as linhas de cada uma, e
 * `lerCelulasDaPlanilha`, que devolve cada célula pelo endereço, com o tipo e o
 * valor cru.
 *
 * Como usar: cada módulo monta a sua `Planilha` com o valor já pronto para a
 * célula — texto como texto, dinheiro como número, data como
 * `{ data: 'AAAA-MM-DD' }` — e chama `exportarPlanilha` no clique do botão. O
 * número deverá entrar como número, e nunca já escrito como `R$ 1.234,56`: é o
 * que deixa quem abre o arquivo somar a coluna, e o texto formatado faria a
 * soma dar zero sem nada acusar. Pela mesma razão a data entra como data do
 * Excel, mostrada como `dd/mm/aaaa`, e não como o texto que a tela escreve.
 *
 * A biblioteca de planilha só é carregada no primeiro clique, pelo `import()`
 * dinâmico de dentro de `exportarPlanilha`, `lerPlanilha` e
 * `lerCelulasDaPlanilha`, e o Vite a
 * separa num arquivo próprio do bundle. Um `import` de valor dela no topo de
 * qualquer arquivo a levaria para o carregamento de todas as telas; aqui ela só
 * entra por `import type`, que some na compilação. É também por isso que
 * `exportarPlanilha` devolve uma promessa, que rejeita quando esse arquivo não
 * chega — sem rede, ou depois de uma publicação que trocou o nome dele —, e
 * quem chama deverá mostrar a falha em vez de engoli-la.
 *
 * O conteúdo das células é dado da família — CPF, salário, observação —, e vai
 * para o arquivo porque foi isso que a pessoa pediu ao clicar. Nada dele
 * deverá ir para `console`, telemetria ou texto de erro: a mensagem de falha
 * que quem chama mostra fala do arquivo, nunca do que estava nele.
 */
import type { CellObject, WorkSheet } from 'xlsx';

/** Uma data para a célula, em `AAAA-MM-DD`, gravada como data do Excel. */
export interface DataDaPlanilha {
  data: string;
}

/**
 * O que cabe numa célula.
 *
 * `null` deixa a célula vazia — não é o mesmo que o texto `—` da tela, que
 * numa coluna de números viraria texto no meio deles.
 */
export type CelulaDaPlanilha = string | number | DataDaPlanilha | null;

/** Uma aba do arquivo: nome, cabeçalhos e linhas, na ordem da tela. */
export interface AbaDaPlanilha {
  /**
   * Nome da aba. O Excel recusa mais de 31 caracteres e os sinais
   * `[ ] : * ? / \`, e a biblioteca lança erro antes de gerar o arquivo.
   */
  nome: string;
  /** A primeira linha da aba, uma célula por coluna. */
  cabecalhos: readonly string[];
  /** Uma linha por registro, com uma célula por cabeçalho, na mesma ordem. */
  linhas: readonly (readonly CelulaDaPlanilha[])[];
  /**
   * A largura de cada coluna, em caracteres, na ordem dos cabeçalhos. Ausente,
   * o Excel usa a largura padrão dele.
   */
  larguras?: readonly number[];
}

/** O arquivo a gerar: o nome e as abas, na ordem em que aparecem no Excel. */
export interface Planilha {
  /** Nome do arquivo baixado, já com a extensão `.xlsx`. */
  nomeDoArquivo: string;
  /** As abas, a primeira aberta quando o arquivo abre. */
  abas: readonly AbaDaPlanilha[];
}

/** O formato de exibição das células de data. */
const FORMATO_DE_DATA = 'dd/mm/yyyy';

/** Milissegundos de um dia, para contar os dias até a data. */
const MILISSEGUNDOS_POR_DIA = 86_400_000;

/** O dia zero da contagem de datas do Excel, 30/12/1899, em UTC. */
const DIA_ZERO_DO_EXCEL = Date.UTC(1899, 11, 30);

/**
 * Converte a data ISO no número de série com que o Excel guarda datas.
 *
 * A conta é feita sobre os três números do texto, com `Date.UTC`, e nunca com
 * `new Date('AAAA-MM-DD')` no fuso do navegador: a meia-noite em UTC volta, no
 * fuso do escritório, como o dia anterior, e a planilha sairia com todas as
 * datas um dia antes.
 *
 * @param iso - A data, em `AAAA-MM-DD`.
 * @returns O número de série, ou `null` quando o texto não é uma data ISO.
 */
function serialDoExcel(iso: string): number | null {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (partes === null) return null;
  const [, ano, mes, dia] = partes;
  return (
    (Date.UTC(Number(ano), Number(mes) - 1, Number(dia)) - DIA_ZERO_DO_EXCEL) /
    MILISSEGUNDOS_POR_DIA
  );
}

/**
 * Traduz o valor de uma célula para o que a biblioteca grava.
 *
 * Texto e número passam como estão, e `null` segue nulo, que a biblioteca
 * pula. A data vira uma célula numérica com o formato `dd/mm/aaaa`; o texto que
 * não é data ISO vai como texto, para nada sumir do arquivo em silêncio.
 *
 * @param celula - O valor montado pelo módulo.
 * @returns O valor, ou a célula pronta, na forma que `aoa_to_sheet` aceita.
 */
function paraOLivro(celula: CelulaDaPlanilha): string | number | CellObject | null {
  if (celula === null || typeof celula !== 'object') return celula;
  const serial = serialDoExcel(celula.data);
  if (serial === null) return celula.data;
  return { t: 'n', v: serial, z: FORMATO_DE_DATA };
}

/**
 * Gera o `.xlsx` da `Planilha` e o entrega ao navegador como download.
 *
 * Quem chama é o botão de exportar de uma tela, no clique. A primeira chamada
 * espera o arquivo da biblioteca chegar pela rede; as seguintes o encontram já
 * carregado. Cada aba tem uma linha de cabeçalho seguida das `linhas` na ordem
 * recebida — a planilha não ordena nem filtra, e sai na ordem que a tela
 * passar. Duas abas com o mesmo nome fazem a biblioteca lançar erro, e a
 * promessa rejeita sem baixar nada.
 *
 * @param planilha - O arquivo a gerar, com as células já montadas.
 * @returns Uma promessa que cumpre depois de o download ser disparado, e que
 *   rejeita quando a biblioteca não pôde ser carregada.
 */
export async function exportarPlanilha(planilha: Planilha): Promise<void> {
  const XLSX = await import('xlsx');
  const livro = XLSX.utils.book_new();
  for (const aba of planilha.abas) {
    const folha = XLSX.utils.aoa_to_sheet([
      [...aba.cabecalhos],
      ...aba.linhas.map((linha) => linha.map(paraOLivro)),
    ]);
    if (aba.larguras !== undefined) folha['!cols'] = aba.larguras.map((wch) => ({ wch }));
    XLSX.utils.book_append_sheet(livro, folha, aba.nome);
  }
  XLSX.writeFile(livro, planilha.nomeDoArquivo);
}

/** Uma planilha aberta: os nomes das abas e as linhas de cada uma. */
export interface PlanilhaLida {
  /** Os nomes das abas, na ordem do arquivo. */
  abas: readonly string[];
  /**
   * As linhas de uma aba, uma lista de células por linha, com `null` na célula
   * vazia. Texto vem como texto, número como número e data como `Date`.
   */
  linhasDa: (aba: string) => unknown[][];
}

/**
 * Abre um `.xlsx` ou `.xlsm` escolhido pela pessoa, sem enviá-lo a lugar nenhum.
 *
 * Quem chama é o "Importar Folha XLSX" do Fechamento. O arquivo é lido com as
 * datas como `Date` (`cellDates`), e cada aba vira linhas de células, com as
 * vazias em `null` (`header: 1, defval: null`) — a regra de quem lê procura a
 * célula vazia pela posição, e a linha encurtada desalinharia as colunas.
 *
 * @param arquivo - O arquivo escolhido.
 * @returns As abas e a leitura das linhas de cada uma; a aba que não existe dá
 *   nenhuma linha.
 * @throws O erro da biblioteca quando o arquivo não é uma planilha que ela lê.
 */
export async function lerPlanilha(arquivo: Blob): Promise<PlanilhaLida> {
  const XLSX = await import('xlsx');
  const livro = XLSX.read(await arquivo.arrayBuffer(), { type: 'array', cellDates: true });
  return {
    abas: livro.SheetNames,
    linhasDa: (aba) => {
      const folha = livro.Sheets[aba];
      return folha === undefined
        ? []
        : XLSX.utils.sheet_to_json<unknown[]>(folha, { header: 1, defval: null });
    },
  };
}

/** Uma célula como a biblioteca a guarda: o tipo e o valor, sem conversão. */
export interface CelulaLida {
  /** `s` texto, `n` número (hora é fração do dia), `b` booleano, `d` data, `e` erro, `z` vazia. */
  tipo: string;
  valor: unknown;
}

/** O retângulo usado de uma aba, com linha e coluna contadas a partir de zero. */
export interface IntervaloDaAba {
  linhaInicial: number;
  linhaFinal: number;
  colunaInicial: number;
  colunaFinal: number;
}

/** Uma planilha aberta para leitura por endereço. */
export interface CelulasLidas {
  /** Os nomes das abas, na ordem do arquivo. */
  abas: readonly string[];
  /** A célula na linha e coluna (a partir de zero), ou `null` quando vazia ou fora da aba. */
  celula: (aba: string, linha: number, coluna: number) => CelulaLida | null;
  /** O intervalo usado da aba, ou `null` quando a aba não existe ou não declara intervalo. */
  intervalo: (aba: string) => IntervaloDaAba | null;
}

/**
 * Diz se um valor guardado na aba é uma célula, e não uma das chaves `!…`.
 *
 * @param valor - O que a aba guarda num endereço.
 * @returns Verdadeiro quando o valor tem o tipo `t` de uma célula.
 */
function ehCelula(valor: unknown): valor is CellObject {
  return typeof valor === 'object' && valor !== null && 't' in valor;
}

/**
 * Abre uma planilha para leitura célula a célula, pelo endereço.
 *
 * Quem chama é a importação do extrato do Secullum, que lê posições fixas —
 * coluna 14 é o crédito, 20 o débito — e precisa saber se a célula é texto
 * (`"24:53"`) ou número (a hora como fração do dia, `0,29166…`). Por isso o
 * arquivo é aberto com `cellDates: false`: com datas ligadas, a célula de hora
 * viraria `Date` e o tempo se perderia.
 *
 * @param arquivo - O arquivo escolhido.
 * @returns As abas e a leitura por endereço e por intervalo.
 * @throws O erro da biblioteca quando o arquivo não é uma planilha que ela lê.
 */
export async function lerCelulasDaPlanilha(arquivo: Blob): Promise<CelulasLidas> {
  const XLSX = await import('xlsx');
  const livro = XLSX.read(await arquivo.arrayBuffer(), { type: 'array', cellDates: false });
  const aba = (nome: string): WorkSheet | undefined => livro.Sheets[nome];

  return {
    abas: livro.SheetNames,
    celula: (nome, linha, coluna) => {
      const guardado: unknown = aba(nome)?.[XLSX.utils.encode_cell({ r: linha, c: coluna })];
      return ehCelula(guardado) ? { tipo: guardado.t, valor: guardado.v } : null;
    },
    intervalo: (nome) => {
      const referencia = aba(nome)?.['!ref'];
      if (referencia === undefined) return null;
      const { s, e } = XLSX.utils.decode_range(referencia);
      return { linhaInicial: s.r, linhaFinal: e.r, colunaInicial: s.c, colunaFinal: e.c };
    },
  };
}
